import React from "react";
import { InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Checkbox, FormGroup, FormControlLabel } from "@mui/material";

import { DynamicFormElement } from "../Shared/DynamicFormElement";
import { ActionButton, ActionHandler, CreateAction, EmptyActionData } from "./ActionButton";
import { FanboxStyling } from "./CSSUtilities";
import { FanboxColorName } from "../Shared/Utility/Color";





export type DynamicFormProps = {
    elements: DynamicFormElement[];
    submissionButtonText: string;
    submissionColor: FanboxColorName;
    submissionTextColor: FanboxColorName;
    submissionDisableColor: FanboxColorName;
    cancelButtonText: string;
    cancelColor: FanboxColorName;
    cancelTextColor: FanboxColorName;
    submit: (data: { [key: string]: string }) => void;
    cancel: () => void;
    height: number;
    width: number;
    background: string;
    fontSize: number;
    textColor: string;
    controlColor: string;
    controlTextColor: string;
    linkColor: string;
}

export type TextDisplayProps = {
    fontFamily: string;
    fontSize: number;
    fontWeight: number;
    color: string;
    linkColor: string;
    linkFontWeight: number;
}

export const StringToTextAndLinks = (rawText: string, displayProps: TextDisplayProps): JSX.Element[] => {
    const ret = Array<JSX.Element>();
    var text = rawText;

    while (text.includes('](')) {
        const midIndex = text.indexOf('](');
        const startIndex = text.lastIndexOf(']', midIndex);
        const endIndex = text.indexOf(')', midIndex);

        if (midIndex > 0 && startIndex > 0 && endIndex > 0) {
            ret.push(<span style={{
                fontFamily: displayProps.fontFamily,
                fontSize: displayProps.fontSize + 'px',
                fontWeight: displayProps.fontWeight,
                color: displayProps.color
            }} >{text.slice(0, startIndex)}</span>);
            const linkText = text.slice(startIndex + 1, midIndex);
            const link = text.slice(midIndex + 2, endIndex);
            text = text.slice(endIndex + 1);
            ret.push(<a href={link} style={{
                fontFamily: displayProps.fontFamily,
                fontSize: displayProps.fontSize,
                fontWeight: displayProps.linkFontWeight,
                color: displayProps.linkColor,
            }} >{linkText}</a>)
        }
        else {
            ret.push(<span style={{
                fontFamily: displayProps.fontFamily,
                fontSize: displayProps.fontSize + 'px',
                fontWeight: displayProps.fontWeight,
                color: displayProps.color
            }} >{text}</span>);
            text = '';
        }

    }
    if (text !== '') {
        ret.push(<span style={{
            fontFamily: displayProps.fontFamily,
            fontSize: displayProps.fontSize + 'px',
            fontWeight: displayProps.fontWeight,
            color: displayProps.color
        }} >{text}</span>);
    }


    return ret;

}

export const DynamicForm = ({ elements, submissionButtonText, cancelButtonText, submit, cancel, height, width, background, fontSize, textColor, controlColor, controlTextColor, submissionColor, submissionDisableColor, submissionTextColor, cancelColor, cancelTextColor, linkColor }: DynamicFormProps): JSX.Element => {

    const mandatoryFields = React.useRef<{ [key: string]: boolean }>((() => {
        const ret: { [key: string]: boolean } = {};
        elements.forEach((element) => {
            if (element.elementType !== 'file')
                ret[element.name] = element.mandatory === true;
        })
        return ret;
    })());

    const mandatoryFiles = React.useRef<{ [key: string]: boolean }>((() => {
        const ret: { [key: string]: boolean } = {};
        elements.forEach((element) => {
            if (element.elementType === 'file')
                ret[element.name] = element.mandatory === true;
        })
        return ret;
    })());


    const formDataRef = React.useRef<{ [key: string]: string }>((() => {
        const ret: { [key: string]: string } = {};
        elements.forEach((element) => {
            if (element.elementType !== 'file')
                ret[element.name] = element.defaultValue;
        })
        return ret;
    })());
    const [formData, setFormData] = React.useState<{ [key: string]: string }>(formDataRef.current);

    const fileDataRef = React.useRef<{ [key: string]: ArrayBuffer }>({});

    const [fileData, setFileData] = React.useState<{ [key: string]: ArrayBuffer }>({});

    const isFormComplete = (): boolean => {
        var complete = true;
        Object.keys(mandatoryFields.current).forEach((key) => {
            if (mandatoryFields.current[key]) {
                const test = (formDataRef.current[key] !== undefined ? (formDataRef.current[key] !== '' && formDataRef.current[key] !== 'false') : false);
                console.log('test : key =' + key + ' value=' + formDataRef.current[key] + ' outcome = ' + test);
                complete = complete && test;
            }
        });

        console.log('complete:isFormComplete (fields) = ' + complete);

        Object.keys(mandatoryFiles.current).forEach((key) => {
            if (mandatoryFiles.current[key]) {
                complete = complete && (fileDataRef.current[key] !== undefined);
            }
        })
        console.log('complete:isFormComplete (fields) = ' + complete);
        console.trace();
        return complete;
    }

    const [isComplete, setIsComplete] = React.useState<boolean>();
    React.useEffect(()=> {setIsComplete(isFormComplete())}, [])

    const setStringValue = (name: string, value: string) => {
        const nObj: { [key: string]: string } = {};
        Object.keys(formData).forEach((key) => {
            nObj[key] = formData[key];
        });
        formDataRef.current = nObj;
        nObj[name] = value;
        const c = isFormComplete();
        console.log('setting form complete =' + c);
        setIsComplete(c);
        setFormData(nObj);

    }

    const getStringValue = (name: string): string => { return formData[name]; }

    const fileChangeHandler = async (name: string, event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.files !== null) {
            const files = event.target.files;
            if (files.length > 0) {
                const file = files[0];
                const reader = new FileReader();

                reader.onload = (event) => {
                    const arrayBuffer = event.target?.result as ArrayBuffer;
                    const nObj: { [key: string]: ArrayBuffer } = {};
                    Object.keys(fileData).forEach((key) => {
                        nObj[key] = fileData[key];
                    });
                    nObj[name] = arrayBuffer;
                    fileDataRef.current = nObj;
                    setFileData(nObj);
                };

                reader.readAsArrayBuffer(file);
                setIsComplete(isFormComplete());
            }
        }
    }

    const submitHandler : ActionHandler = (_action) => {
        if (isComplete) {
            Object.keys(fileData).forEach((key) => {
                const buffer = fileData[key];
                const bytes = new Uint8Array(buffer);
                var encodeHex = '';
                bytes.forEach((byte) => {
                    const v = byte % 256;
                    if (v < 16)
                        encodeHex += '0';
                    encodeHex += v.toString(16);
                });
                formData[key] = encodeHex;
            });
            submit(formData);
        }
        return true;
    }

    const submitAction = CreateAction('submit', [], submitHandler);

    const submitStyling : FanboxStyling = {
        width : (width * 0.5 - 30),
        height : fontSize * 2,
        boundary : {
            margin : {
                quadType : 'margin',
                top : 10,
                left : 10,
                right : 10,
                bottom : 10
            }
        },
        color : {
            foreground : submissionTextColor,
            background : isComplete ? submissionColor : submissionDisableColor
        },
        font : {
            size : fontSize,
            weight : 'regular'
        }
    }

    const cancelHandler : ActionHandler = (_action) => {
        cancel();
        return true;
    }

    const cancelAction = CreateAction('cancel', [], cancelHandler);

    const cancelStyling : FanboxStyling= {
        width : (width * 0.5) -30,
        height : fontSize * 2,
        boundary : {
            margin : {
                quadType : 'margin',
                top : 10,
                left : 20,
                right : 10,
                bottom : 10
            }
        },
        font : {
            size : fontSize,
            weight : 'regular'
        },
        color : {
            foreground : cancelTextColor,
            background : cancelColor
        }
    }

    return (
        <div style={{
            width: width + 'px',
            minWidth: width + 'px',
            maxWidth: width + 'px',
            height: height + 'px',
            minHeight: height + 'px',
            maxHeight: height + 'px',
            backgroundColor: background,
            paddingTop : '20px',
            overflow: 'clip',
        }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                width: width + 'px',
                minWidth: width + 'px',
                maxWidth: width + 'px',
                textAlign: 'left',
            }}>
                {
                    elements.map((element, index) => {
                        if (element.elementType === 'heading') {
                            return <span style={{
                                fontFamily: 'roboto',
                                fontSize: (2 * fontSize) + 'px',
                                fontWeight: 900,
                                color: textColor,
                                marginTop: '10px',
                                marginLeft: '30px',
                                marginRight: '30px',
                                marginBottom: '10px'
                            }} key={'DynamicFormElement' + index}>{element.label}</span>
                        }
                        else if (element.elementType === 'text') {
                            const textElements = StringToTextAndLinks(element.label, {
                                fontFamily: 'roboto',
                                fontSize: fontSize,
                                fontWeight: 400,
                                color: textColor,
                                linkColor: linkColor,
                                linkFontWeight: 700
                            });
                            return <div style={{
                                marginTop: '10px',
                                marginLeft: '30px',
                                marginRight: '30px',
                                marginBottom: '10px'
                            }} key={'DynamicFormElement' + index}>{textElements}</div>
                        }
                        else if (element.elementType === 'image') {
                            return <div style={{
                                width: width + 'px',
                                minWidth: width + 'px',
                                maxWidth: width + 'px',
                                marginTop: '10px',
                                marginLeft: '30px',
                                marginRight: '30px',
                                marginBottom: '10px',
                                backgroundImage: 'url(' + (element.src ?? '') + ')',
                                backgroundPosition: 'center'
                            }} key={'DynamicFormElement' + index}></div>
                        }
                        else if (element.elementType === 'textbox') {
                            return <TextField required={element.mandatory === true} onChange={(ev) => { setStringValue(element.name, ev.target.value); }} value={getStringValue(element.name)} autoFocus={true} sx={{
                                backgroundColor: controlColor,
                                color: controlTextColor,
                                borderRadius: '8px',
                                marginTop: '10px',
                                marginLeft: '30px',
                                marginRight: '30px',
                                marginBottom: '10px',
                                fontSize: fontSize,
                            }} label={element.label} key={'DynamicFormElement' + index}/>
                        }
                        else if (element.elementType === 'combo') {
                            return <FormGroup key={'DynamicFormElement' + index}><InputLabel id={'dynamic-form-combo-input-label-' + element.name}>{element.label}</InputLabel>
                                <Select
                                required = {element.mandatory === true}
                                    labelId={'dynamic-form-combo-input-label-' + element.name}
                                    id={"dynamic-form-combo-" + element.name}
                                    onChange={(ev: SelectChangeEvent) => { setStringValue(element.name, ev.target.value) }}
                                    sx={{
                                        backgroundColor: controlColor,
                                        fontSize: fontSize,
                                        borderRadius: '8px',
                                        marginTop: '10px',
                                        marginLeft: '30px',
                                        marginRight: '30px',
                                        marginBottom: '10px',
                                    }}
                                    MenuProps={{
                                        sx: {
                                            color: controlTextColor,

                                        }
                                    }}
                                >
                                    {element.options.map((row) => {
                                        return <MenuItem value={row.value}>{row.name}</MenuItem>
                                    })}

                                </Select></FormGroup>
                        }
                        else if (element.elementType === 'check') {
                            return <FormControlLabel
                                control={<Checkbox required={element.mandatory === true} onChange={(ev) => {
                                    const value = ev.target.checked ? 'true' : 'false';
                                    setStringValue(element.name, value)
                                }} />}
                                label={element.label} checked={getStringValue(element.name) === 'true'} key={'DynamicFormElement' + index}/>
                        }
                        else {
                            return <div key={'DynamicFormElement' + index}>
                                <span style={{
                                    fontFamily: 'roboto',
                                    fontSize: fontSize + 'px',
                                    fontWeight: 400,
                                    color: textColor,
                                    marginTop: '10px',
                                    marginLeft: '30px',
                                    marginRight: '30px',
                                    marginBottom: '10px'
                                }} >{element.label}</span>
                                <input type='file' onChange={(event) => { fileChangeHandler(element.name, event); }} id={'dynamicform_fileinput_' + element.name} />
                            </div>
                        }
                    })
                }

                <div style={{
                    display: 'flex',
                    flexDirection: 'row',
                    width: width + 'px',
                    minWidth: width + 'px',
                    maxWidth: width + 'px',

                }}>
                    <ActionButton text={cancelButtonText}
                    action={cancelAction}
                    actionData={EmptyActionData}
                    styling = {cancelStyling}
                    prompt={'left'}
                        
                        />
                    <ActionButton text={submissionButtonText}
                        action={submitAction}
                        actionData={EmptyActionData}
                        styling={submitStyling}
                        prompt={'left'}
                         />
                </div>

            </div>
        </div>
    );
}